"use client";

import React, { useState, useEffect, useCallback, type FormEvent } from "react";
import { Loader, Send, Info, Check, Plus, X } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const BRAND_GREEN = "#436C1A";
const BRAND_YELLOW = "#FCC22F";
const PIZZA_OPTIONS = ["Marinara (Vegan)", "Margherita (Vegetarisch)", "Verdechiosa (Vegetarisch)", "Pera (Vegetarisch)", "Rossochiosa (Meat Lovers)", "Pistacchiosa (Meat Lovers)", "Forestella (Meat Lovers)", "Stella dolce (Sweet)"] as const;
type PizzaOption = (typeof PIZZA_OPTIONS)[number];
const PIZZA_IMAGES: Record<PizzaOption, string> = {
  "Marinara (Vegan)": "https://namo-catering.de/wp-content/uploads/2026/02/Marinara.remini-enhanced.jpg",
  "Margherita (Vegetarisch)": "https://namo-catering.de/wp-content/uploads/2026/02/Margherita.remini-enhanced.jpg",
  "Verdechiosa (Vegetarisch)": "https://namo-catering.de/wp-content/uploads/2026/02/Verdechiosa.remini-enhanced.jpg",
  "Pera (Vegetarisch)": "https://namo-catering.de/wp-content/uploads/2026/02/Pera.remini-enhanced.jpg",
  "Rossochiosa (Meat Lovers)": "https://namo-catering.de/wp-content/uploads/2026/02/Rossochiosa.remini-enhanced.jpg",
  "Pistacchiosa (Meat Lovers)": "https://namo-catering.de/wp-content/uploads/2026/02/Pistacchiosa.remini-enhanced.jpg",
  "Forestella (Meat Lovers)": "https://namo-catering.de/wp-content/uploads/2026/02/Forestella.remini-enhanced.jpg",
  "Stella dolce (Sweet)": "https://namo-catering.de/wp-content/uploads/2026/02/Stella-dolce.remini-enhanced.jpg"
};
const PIZZA_DESCRIPTIONS: Record<string, string> = {
  Marinara: "Aromatische Tomatensauce, feiner Knoblauch, frischer Oregano, frisches Basilikum und ein Hauch hochwertiges Olivenöl. Ein purer, perfekter Pizza-Klassiker.",
  Margherita: "Der Klassiker mit leckerer Tomatensauce, zart schmelzendem Mozzarella, frischem Basilikum und hochwertigem Olivenöl.",
  Verdechiosa: "Cremiges Pesto mit einem Hauch Zitrone, Mozzarella und sonnengereifte Tomaten. Abgerundet mit frischem Basilikum, Pecorino und feinem Olivenöl – Sommer auf dem Teller.",
  Pera: "Süße Birne trifft auf herzhaften Gorgonzola, zarten Mozzarella, feinen Honig, frischem Basilikum und hochwertiges Olivenöl – ein Genussmoment der besonderen Art.",
  Forestella: "Leckere, süßliche Tomatensauce trifft auf zart schmelzendem Mozzarella, verfeinert mit frischem Basilikum und hochwertiges Olivenöl. Getoppt mit hauchdünnem Prosciutto, knackigem Rucola und feinen Pecorino!",
  Rossochiosa: "Eine leidenschaftliche Komposition aus aromatischer Tomatensauce, zartschmelzendem Büffelmozzarella, würziger neapolitanischer Salami, süßer roter Spitzpaprika, frischem Basilikum, hochwertigem Olivenöl und einem feinen Hauch Chili-Honig. Scharf. Süß. Sinnlich.",
  Pistacchiosa: "Cremige Pistaziencreme trifft auf zart schmelzenden Mozzarella und feine Mortadella. Getoppt mit gehackten Pistazien, etwas Pecorino und einem Hauch Olivenöl. Vollendeter Genuss in Grün.",
  "Stella dolce": "Süße Dessertpizza mit cremigem Nutella und knackigem Haselnuss-Krokant – jede Sternspitze ist mit zartschmelzendem Nutella gefüllt."
};
const ContactFormContent = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    control,
    watch,
    setValue
  } = useWvcForm();
  const [infoPizza, setInfoPizza] = useState<PizzaOption | null>(null);
  const eventType = watch("event_type");
  const isPrivateEvent = eventType === "Privates Event";
  const maxPizzas = React.useMemo(() => {
    if (!eventType) return 0;
    if (eventType === "Berufliches Event") return 5;
    if (eventType === "Hochzeit") return 6;
    return 4;
  }, [eventType]);
  const pizzasValue = watch("pizzas") as PizzaOption[] | undefined;
  const parsePizza = (pizza: PizzaOption) => {
    const hasMeta = pizza.includes(" (");
    const title = hasMeta ? pizza.split(" (")[0] : pizza;
    const meta = hasMeta ? pizza.split(" (")[1].replace(")", "") : "";
    return {
      title,
      meta
    };
  };
  useEffect(() => {
    if (!isPrivateEvent) setValue("privateEventSetup", "");
  }, [isPrivateEvent, setValue]);
  useEffect(() => {
    if (!eventType) {
      setValue("pizzas", []);
      return;
    }
    const current = Array.isArray(pizzasValue) ? pizzasValue : [];
    if (current.length > maxPizzas) {
      setValue("pizzas", current.slice(0, maxPizzas));
    }
  }, [eventType, maxPizzas, pizzasValue, setValue]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center p-8 space-y-4 bg-primary/5 rounded-2xl border border-primary/10 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-foreground-primary mb-2">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-display font-semibold text-primary">Vielen Dank!</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage || "Ihre Anfrage wurde erfolgreich gesendet. Wir werden uns in Kürze bei Ihnen melden."}
        </p>
      </div>;
  }
  return <div className="space-y-8">
      {}
      <div className="space-y-4">
        <h4 className="text-sm font-extrabold text-slate-900">Kontaktdaten</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <FormField control={control} name="name" rules={{
          required: "Bitte geben Sie Ihren Namen ein."
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Ihr Name</FormLabel>
                <FormControl>
                  <Input placeholder="Max Mustermann" {...field} />
                </FormControl>
                <FormDescription>Vor- und Nachname</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "E-Mail-Adresse ist erforderlich.",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>E-Mail-Adresse</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="max@beispiel.de" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <FormField control={control} name="phone" rules={{
          required: "Telefonnummer ist erforderlich.",
          pattern: {
            value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
            message: "Bitte geben Sie eine gültige Telefonnummer ein."
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Telefonnummer</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+49 123 456789" {...field} />
                </FormControl>
                <FormDescription>Wir nutzen dies zur Bestätigung.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-4">
        <h4 className="text-sm font-extrabold text-slate-900">Wohnadresse</h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <FormField control={control} name="homeStreet" rules={{
            required: "Straße ist erforderlich."
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Straße</FormLabel>
                  <FormControl>
                    <Input placeholder="Musterstraße" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="homeHouseNumber" rules={{
          required: "Hausnummer ist erforderlich."
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Hausnr.</FormLabel>
                <FormControl>
                  <Input placeholder="12A" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <FormField control={control} name="homePostalCode" rules={{
          required: "PLZ ist erforderlich."
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>PLZ</FormLabel>
                <FormControl>
                  <Input inputMode="numeric" placeholder="50667" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="md:col-span-2">
            <FormField control={control} name="homeCity" rules={{
            required: "Ort ist erforderlich."
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Ort</FormLabel>
                  <FormControl>
                    <Input placeholder="Köln" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>
      </div>

      {}
      <div className="space-y-4">
        <h4 className="text-sm font-extrabold text-slate-900">Event-Location</h4>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <FormField control={control} name="eventStreet" rules={{
            required: "Straße der Location ist erforderlich."
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Straße</FormLabel>
                  <FormControl>
                    <Input placeholder="Veranstaltungsweg" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="eventHouseNumber" rules={{
          required: "Hausnummer ist erforderlich."
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Hausnr.</FormLabel>
                <FormControl>
                  <Input placeholder="5" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <FormField control={control} name="eventPostalCode" rules={{
          required: "PLZ ist erforderlich."
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>PLZ</FormLabel>
                <FormControl>
                  <Input inputMode="numeric" placeholder="50667" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="md:col-span-2">
            <FormField control={control} name="eventCity" rules={{
            required: "Ort ist erforderlich."
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Ort</FormLabel>
                  <FormControl>
                    <Input placeholder="Köln" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <FormField control={control} name="event_type" rules={{
          required: "Bitte wählen Sie eine Veranstaltungsart."
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Art der Veranstaltung</FormLabel>
                <FormControl>
                  <div className="relative">
                    <select className={cn("flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none", !field.value && "text-muted-foreground")} {...field}>
                      <option value="" disabled>
                        Bitte wählen
                      </option>
                      <option value="Privates Event">Privates Event</option>
                      <option value="Berufliches Event">Berufliches Event</option>
                      <option value="Hochzeit">Hochzeit</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                      <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="event_date" render={({
          field
        }) => <FormItem>
                <FormLabel>Gewünschtes Datum</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormDescription>Falls Sie bereits ein Datum haben</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <FormField control={control} name="guests" rules={{
          required: "Anzahl der Gäste ist erforderlich.",
          min: {
            value: 1,
            message: "Mindestens 1 Gast."
          },
          max: {
            value: 500,
            message: "Maximal 500 Gäste."
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Anzahl der Gäste</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="z.B. 50" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {isPrivateEvent && <FormField control={control} name="privateEventSetup" rules={{
          required: "Bitte wählen Sie ein Setup."
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel>Setup (nur privates Event)</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <select className={cn("flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none", !field.value && "text-muted-foreground")} {...field}>
                        <option value="" disabled>
                          Bitte wählen
                        </option>
                        <option value="Light Setup">
                          Light Setup (Küche wird vom Kunden gestellt)
                        </option>
                        <option value="Full Setup">
                          Full Setup (Küchenmodul wird von uns Namo gestellt)
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />}
        </div>
      </div>

      {}
      <FormField control={control} name="pizzas" rules={{
      validate: (value: PizzaOption[] | undefined) => !eventType || (value?.length ?? 0) <= maxPizzas || `Bitte maximal ${maxPizzas} Pizzen auswählen`
    }} render={({
      field
    }) => {
      const selected: PizzaOption[] = Array.isArray(field.value) ? field.value as PizzaOption[] : [];
      const toggle = (pizza: PizzaOption) => {
        if (!eventType) return;
        if (selected.includes(pizza)) {
          field.onChange(selected.filter(p => p !== pizza));
          return;
        }
        if (selected.length >= maxPizzas) return;
        field.onChange([...selected, pizza]);
      };
      return <FormItem>
              <div className="flex items-center justify-between mb-4">
                <FormLabel className="text-xl font-extrabold text-gray-900 m-0">
                  Menüauswahl
                </FormLabel>
                {Boolean(eventType) && <div className="bg-slate-100 text-slate-700 px-4 py-1.5 rounded-full text-sm font-semibold">
                    {selected.length} / {maxPizzas} gewählt
                  </div>}
              </div>

              <FormControl>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {PIZZA_OPTIONS.map(pizza => {
              const checked = selected.includes(pizza);
              const disabledByMax = !checked && selected.length >= maxPizzas;
              const disabled = !eventType || disabledByMax;
              const {
                title,
                meta
              } = parsePizza(pizza);
              const img = PIZZA_IMAGES[pizza];
              return <div key={pizza} role="button" tabIndex={disabled ? -1 : 0} aria-pressed={checked} aria-disabled={disabled} onClick={() => !disabled && toggle(pizza)} onKeyDown={e => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(pizza);
                }
              }} className={cn("group relative rounded-2xl outline-none overflow-hidden transition-all duration-300 will-change-transform", disabled ? "opacity-50 cursor-not-allowed grayscale-[0.25]" : "cursor-pointer", checked ? "border-2 border-[#436C1A] shadow-[0_10px_30px_rgba(67,108,26,0.18)]" : "border-2 border-slate-100 shadow-sm", !disabled && "hover:-translate-y-1 hover:shadow-lg")}>
                        <div className="w-full aspect-[16/10] bg-slate-100 relative">
                          <img src={img} alt={title} className={cn("absolute inset-0 w-full h-full object-cover transition-transform duration-500", checked ? "scale-[1.03]" : "group-hover:scale-105")} loading="lazy" />

                          <div className={cn("absolute inset-0 transition-opacity duration-300", checked ? "opacity-100" : "opacity-0")} style={{
                    background: `linear-gradient(135deg, ${BRAND_GREEN}22, ${BRAND_YELLOW}22)`
                  }} />

                          <Button type="button" variant="ghost" size="icon" onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setInfoPizza(pizza);
                  }} className="absolute top-3 right-3 p-2 rounded-full z-10 shadow-sm bg-white/75 backdrop-blur text-slate-600 hover:text-[#436C1A] hover:bg-white transition-colors" title="Details anzeigen">
                            <Info size={18} />
                          </Button>
                        </div>

                        <div className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h4 className="font-extrabold text-gray-900 text-lg leading-tight">
                                {title}
                              </h4>
                              {meta && <p className="text-sm font-semibold text-[#436C1A] mt-1">
                                  {meta}
                                </p>}
                            </div>

                            <div className={cn("shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-full transition-all duration-300", checked ? "bg-[#436C1A] text-white scale-100" : "bg-slate-100 text-slate-500 scale-95 group-hover:scale-100")} style={{
                      boxShadow: checked ? "0 10px 20px rgba(67,108,26,0.25)" : undefined
                    }} aria-hidden="true">
                              {checked ? <Check size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className={cn("h-full rounded-full transition-all duration-500", checked ? "w-full" : "w-0")} style={{
                        background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_YELLOW})`
                      }} />
                            </div>
                          </div>

                          {!eventType && <div className="mt-3 text-xs font-semibold text-slate-500">
                              Bitte zuerst die Art der Veranstaltung auswählen.
                            </div>}
                        </div>

                        <div className={cn("pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-300", checked ? "opacity-100" : "opacity-0")} style={{
                  boxShadow: `inset 0 0 0 2px ${BRAND_GREEN}55, 0 0 0 6px ${BRAND_GREEN}14`
                }} />
                      </div>;
            })}
                </div>
              </FormControl>

              <FormMessage className="mt-3 text-red-500 font-medium" />
            </FormItem>;
    }} />

      {}
      <FormField control={control} name="message" rules={{
      required: "Bitte geben Sie eine Nachricht ein.",
      maxLength: {
        value: 1000,
        message: "Maximal 1000 Zeichen."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Nachricht / Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Erzählen Sie uns von Ihrem Event, Menüwünschen, etc." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] h-12 text-base font-semibold shadow-md hover:scale-[1.02] transition-transform duration-200">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Wird gesendet...
            </> : "Anfrage absenden"}
        </Button>
      </div>

      {}
      {infoPizza && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setInfoPizza(null)} />
          <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" role="dialog" aria-modal="true">
            <div className="h-2 w-full" style={{
          background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_YELLOW})`
        }} />
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-3xl font-extrabold text-gray-900 mb-2">
                    {parsePizza(infoPizza).title}
                  </h3>
                  {parsePizza(infoPizza).meta && <span className="inline-flex items-center rounded-full bg-[#436C1A]/10 px-3 py-1 text-xs font-bold text-[#436C1A]">
                      {parsePizza(infoPizza).meta}
                    </span>}
                </div>

                <Button variant="ghost" size="icon" onClick={() => setInfoPizza(null)} className="shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors" aria-label="Schließen">
                  <X size={20} />
                </Button>
              </div>

              <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 mb-6">
                <img src={PIZZA_IMAGES[infoPizza]} alt={parsePizza(infoPizza).title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
              </div>

              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {PIZZA_DESCRIPTIONS[parsePizza(infoPizza).title] || "Beschreibung folgt."}
              </p>

              <Button type="button" onClick={() => setInfoPizza(null)} className="w-full py-6 text-lg font-semibold rounded-xl" style={{
            backgroundColor: BRAND_GREEN,
            color: "white"
          }}>
                Schließen
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const handleSubmitWrapper = useCallback(async (e: FormEvent<HTMLFormElement>, handleSubmit: () => Promise<void>) => {
    e.preventDefault();
    await handleSubmit();
  }, []);
  return;
}